/**
 * Search-term highlighter.
 *
 * When a user lands on a page from the Algolia search results (the result links
 * are decorated with `?t=<search term>`), this module:
 *
 *  1. Wraps every occurrence of the term(s) in the page content in <mark> tags,
 *     reusing the same yellow highlight that is used on the search results page
 *     itself (browser default <mark> styling + `.ais-Highlight-highlighted`).
 *  2. Expands any collapsible Alpine.js elements (`x-show` / `x-collapse`)
 *     that currently hide a match, so the highlighted text becomes visible.
 *  3. Smoothly scrolls to the first occurrence.
 *  4. Removes the `?t=` parameter from the URL so it doesn't leak into copy/paste
 *     or analytics.
 *
 * Pure front-end: no template changes needed on the destination pages.
 */

const HIGHLIGHT_CLASS = 'search-term-highlight';
const PARAM_NAME = 't';

// Elements whose text content we never want to touch.
const SKIP_TAGS = new Set([
  'SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'SELECT',
  'CODE', 'PRE', 'SVG', 'MARK', 'IFRAME', 'TEMPLATE', 'CANVAS',
]);

// Containers we don't want to highlight inside (chrome of the page).
const SKIP_CONTAINER_SELECTOR = 'header, nav, footer, [role="navigation"], [role="banner"], [role="contentinfo"], .skip-to-main-content';

/**
 * Escape a string for use inside a RegExp.
 */
const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Split the search term into individual, non-trivial words.
 * Words shorter than 2 chars are dropped to avoid noisy matches.
 */
const tokenize = (term) => {
  return term
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 2);
};

/**
 * Build a single case-insensitive RegExp matching any of the tokens
 * as whole-ish words (uses a unicode-aware word boundary fallback).
 */
const buildRegex = (tokens) => {
  if (tokens.length === 0) return null;
  // Sort by length descending so longer tokens win when they overlap
  // (e.g. "vermoeidheid" wins over "vermoeid" inside the same word).
  const sorted = [...tokens].sort((a, b) => b.length - a.length);
  const pattern = sorted.map(escapeRegExp).join('|');
  // Substring match: highlight any occurrence of a token, even inside a word.
  return new RegExp(`(${pattern})`, 'giu');
};

/**
 * Should we descend into / highlight inside this element?
 */
const shouldSkipElement = (el) => {
  if (!el || el.nodeType !== Node.ELEMENT_NODE) return false;
  if (SKIP_TAGS.has(el.tagName)) return true;
  if (el.classList && el.classList.contains(HIGHLIGHT_CLASS)) return true;
  if (el.closest && el.closest(SKIP_CONTAINER_SELECTOR)) return true;
  if (el.isContentEditable) return true;
  return false;
};

/**
 * Highlight all matches of `regex` inside `root`. Returns a list of the
 * <mark> elements that were inserted, in document order.
 */
const highlightMatches = (root, regex) => {
  const marks = [];

  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node) {
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        if (shouldSkipElement(node.parentElement)) return NodeFilter.FILTER_REJECT;
        // Reset lastIndex because /g regexes are stateful.
        regex.lastIndex = 0;
        return regex.test(node.nodeValue) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    },
  );

  const targets = [];
  let n;
  // Collect first, mutate after — TreeWalker doesn't like live mutations.
  while ((n = walker.nextNode())) targets.push(n);

  for (const textNode of targets) {
    const text = textNode.nodeValue;
    regex.lastIndex = 0;
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let m;
    while ((m = regex.exec(text)) !== null) {
      const start = m.index;
      const end = start + m[1].length;
      if (start > lastIndex) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex, start)));
      }
      const mark = document.createElement('mark');
      mark.className = `${HIGHLIGHT_CLASS} ais-Highlight-highlighted`;
      mark.textContent = text.slice(start, end);
      frag.appendChild(mark);
      marks.push(mark);
      lastIndex = end;
      // Guard against zero-width matches.
      if (m.index === regex.lastIndex) regex.lastIndex++;
    }
    if (lastIndex < text.length) {
      frag.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
    textNode.parentNode.replaceChild(frag, textNode);
  }

  return marks;
};

/**
 * Expand the closest Alpine collapsible ancestors that currently hide `el`.
 *
 * The site uses patterns like:
 *   <div x-data="{ expanded: false }">
 *     <div x-show="expanded" x-collapse>…match here…</div>
 *   </div>
 * and
 *   <ul x-data="{ openFaq1: false, openFaq2: false }">
 *     <div x-show="openFaq3">…match here…</div>
 *   </ul>
 *
 * So whenever we find an ancestor with `x-show="<identifier>"` that is hidden,
 * we flip that identifier to `true` on the surrounding Alpine scope.
 */
const expandCollapsibleAncestors = (el) => {
  let current = el.parentElement;
  while (current) {
    const expr = current.getAttribute && current.getAttribute('x-show');
    if (expr) {
      const isHidden = current.offsetParent === null
        || getComputedStyle(current).display === 'none'
        || current.style.display === 'none'
        || current.hidden;

      // Only handle simple identifier expressions like `expanded` or `openFaq3`.
      const identifier = expr.trim().match(/^([a-zA-Z_$][\w$]*)$/);
      if (isHidden && identifier && window.Alpine && typeof window.Alpine.$data === 'function') {
        try {
          const scope = window.Alpine.$data(current);
          if (scope && identifier[1] in scope) {
            scope[identifier[1]] = true;
          }
        } catch (_) { /* noop */ }
      }
    }

    // Also handle <details> elements just in case.
    if (current.tagName === 'DETAILS' && !current.open) {
      current.open = true;
    }

    current = current.parentElement;
  }
};

/**
 * Scroll the given element into view, accounting for any sticky header.
 */
const scrollToMark = (mark) => {
  // Wait a tick so Alpine has a chance to actually un-hide the ancestors.
  setTimeout(() => {
    const rect = mark.getBoundingClientRect();
    const offset = window.innerHeight / 4;
    window.scrollTo({
      top: window.scrollY + rect.top - offset,
      behavior: 'smooth',
    });
  }, 350);
};

/**
 * Remove the `t` query param from the URL without reloading.
 */
const cleanUrl = () => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM_NAME);
    const newSearch = url.searchParams.toString();
    const newUrl = `${url.pathname}${newSearch ? `?${newSearch}` : ''}${url.hash}`;
    window.history.replaceState(null, '', newUrl);
  } catch (_) { /* noop */ }
};

const run = () => {
  const params = new URLSearchParams(window.location.search);
  const term = params.get(PARAM_NAME);
  if (!term) return;

  const tokens = tokenize(term);
  const regex = buildRegex(tokens);
  if (!regex) {
    cleanUrl();
    return;
  }

  const root = document.querySelector('main') || document.body;
  if (!root) return;

  const marks = highlightMatches(root, regex);

  if (marks.length > 0) {
    marks.forEach(expandCollapsibleAncestors);
    scrollToMark(marks[0]);
  }

  cleanUrl();
};

/**
 * Initialise the highlighter. Waits until Alpine is initialised so that
 * `Alpine.$data()` returns the live reactive scope for collapsibles.
 */
const prepareSearchHighlight = () => {
  const start = () => {
    // Alpine emits `alpine:initialized` once all components are ready.
    if (window.Alpine) {
      // Give Alpine one frame to finish wiring up x-show bindings.
      requestAnimationFrame(run);
    } else {
      document.addEventListener('alpine:initialized', () => requestAnimationFrame(run), { once: true });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
};

export default prepareSearchHighlight;
